import { sanitizeCustomHtml } from "@redotech/order-tracking-common/sanitize-custom-html";
import { TrackingPageCustomHtmlBlock } from "@redotech/order-tracking-common/tracking-page-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import classnames from "classnames";
import { memo, useRef, useState } from "react";
import { BlockServiceFrame } from "../../block-service/block-service-frame";
import { Text } from "../../text";
import { useBlockLiveState } from "../builder/live-block-context";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import { pageRoute, useCustomHtmlContext } from "./custom-html-context";
import * as css from "./custom-html-isolator.module.css";
import { useCustomHtmlTheme } from "./custom-html-theme";
import { MAX_FRAME_HEIGHT_PX } from "./runtime-block-height";
import { useFitHeightReport } from "./use-fit-height-report";

export const CustomHtmlFrame = memo(function CustomHtmlFrame(
  props: TrackingPageCustomHtmlBlock,
) {
  const context = useRequiredContext(TrackingPageContext);
  const isBuilder = context.environment === TrackingPageEnvironment.BUILDER;
  const overlayRef = useRef<HTMLDivElement>(null);
  const [liquidError, setLiquidError] = useState<string | null>(null);
  const blockState = useBlockLiveState(props._id);
  const overlayClicks = isBuilder && !blockState.live;

  const [wasSelected, setWasSelected] = useState(blockState.selected);
  const [generation, setGeneration] = useState(0);
  if (blockState.selected !== wasSelected) {
    setWasSelected(blockState.selected);
    if (wasSelected) {
      setGeneration(generation + 1);
    }
  }

  const theme = useCustomHtmlTheme();
  const reportFitHeight = useFitHeightReport(props);

  const route = pageRoute(context);
  const frameContext = useCustomHtmlContext(props._id);

  return (
    <div
      className={classnames(
        css.wrapper,
        css.frameCell,
        overlayClicks && css.clickThrough,
      )}
    >
      <BlockServiceFrame
        blockId={props._id}
        code={props.code}
        context={frameContext}
        fallbackClassName={css.fallback}
        html={props.html}
        key={generation}
        onError={(error) => {
          console.error("block-service error", props._id, error);
          if (isBuilder && error.kind === "liquid") {
            setLiquidError(error.message ?? "Liquid error");
          }
        }}
        onHeight={(height) => {
          reportFitHeight?.(Math.min(height, MAX_FRAME_HEIGHT_PX));
          if (overlayRef.current) {
            overlayRef.current.style.height = `${height}px`;
          }
        }}
        onMount={() => setLiquidError(null)}
        page={{ route }}
        sanitizeFallback={sanitizeCustomHtml}
        theme={theme}
      />
      {overlayClicks ? (
        <div
          aria-hidden="true"
          className={css.overlay}
          data-block-service-overlay="true"
          ref={overlayRef}
        />
      ) : null}
      {isBuilder && liquidError ? (
        <Text fontSize="xs" textColor="error">
          {liquidError}
        </Text>
      ) : null}
    </div>
  );
});
